import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, first, take, takeUntil } from 'rxjs';
import {
    LgvClientService,
    LgvUserService,
    NotificationService
} from '../../../../core/core.module';
import { LgvModel } from '../../../../data-models/longevity.model';
import { InfoPopup } from '../../../../shared/info-popup/info-popup.component';
import { LgvPricingModel } from '../../models/pricing.model';
import { LgvLoanApp } from '../../../../data-models/lgv-loan-application.model';
import { LgvPricingService } from '../../services/pricing.service';
import { PricingStatus } from '../../../../core/enums/property.enum';
import { PricingCode } from '../../enums/pricing.enum';
import { LockRequestModalComponent } from '../../../../shared/lock-request-modal/lock-request-modal.component';
import { EmailPopup } from '../../../../shared/email-popup/email-popup.component';
import { ContactAgentDialogComponent } from '../../dialogs/contact-agent-dialog/contact-agent-dialog.component';
import { LgvLoanApplicationService } from '../../../../core/services/lgv-loan-application.service';

@Component({
    selector: 'lgv-opportunity-display',
    templateUrl: './opportunity-display.component.html',
    styleUrls: ['./opportunity-display.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpportunityDisplayComponent implements OnInit {
    @Input() opportunity: LgvModel.Opportunity;
    @Input() scenario: LgvPricingModel.PricingScenario;
    @Input() property: LgvModel.Property;
    @Output() quoteLocked = new EventEmitter<any>(null);
    InfoPopupDialog: any;

    get isProspectProperty() {
        return this.property && this.property.associationType === 'Prospect';
    }
    get isOwnedProperty() {
        return this.property && this.property.associationType === 'Owned';
    }

    get isLockButtonShown() {
        return (
            this.scenario?.status?.code == PricingCode.ReadyToLock ||
            this.scenario?.status?.code == PricingCode.LockRequested
        );
    }

    get isLockRequested() {
        return this.scenario?.status?.code == PricingCode.LockRequested;
    }

    get isArchived() {
        return this.scenario?.status?.code == PricingCode.Archived;
    }

    constructor(
        public matDialog: MatDialog,
        public lgvClientSvc: LgvClientService,
        private lgvUserSvc: LgvUserService,
        private lgvPricingSvc: LgvPricingService,
        private notificationSvc: NotificationService,
        private lgvLoanAppSvc: LgvLoanApplicationService
    ) {}

    ngOnInit(): void {}

    abs(value: number): number {
        return Math.abs(value);
    }

    openInfoPopup(key: string): void {
        const dialogRef = this.matDialog.open(InfoPopup, {
            width: '500px',
            data: { mode: key }
        });
    }

    openSavingsInfoPopup(o: LgvModel.Opportunity) {
        let mode =
            o.opportunityInfo.paymentSavings >= 0
                ? 'MonthlyPaymentSavings'
                : 'MonthlyPaymentIncrease';
        this.openInfoPopupWithOpp(mode, o);
    }

    openInfoPopupWithOpp(mode: string, o: LgvModel.Opportunity) {
        const dialogRef = this.matDialog.open(InfoPopup, {
            width: '500px',
            data: { mode: mode, opportunity: o }
        });

        this.lgvClientSvc
            .PostPageClick(`Opportunities Information - ${mode}`)
            .pipe(first())
            .subscribe();
    }

    viewLoanDetails(
        scenario: LgvPricingModel.PricingScenario,
        q: LgvPricingModel.Quote
    ) {
        const dialogRef = this.matDialog.open(InfoPopup, {
            width: '500px',
            data: {
                mode: 'QuoteDetails',
                quoteDetail: q,
                product:
                    q?.parentProduct ??
                    scenario?.response?.products?.find(
                        p => p?.productName == q?.productName
                    ),
                loanAmount: scenario?.request?.loanInformation?.baseLoanAmount
            }
        });
    }

    requestToLock(
        scenario: LgvPricingModel.PricingScenario,
        o: LgvModel.Opportunity
    ) {
        this.lgvClientSvc.OpportunitiesState$.next('Loading');
        const loanApps = this.lgvLoanAppSvc.clientLoanApplication$.value;
        const dialogRef = this.matDialog.open(LockRequestModalComponent, {
            width: '550px',
            disableClose: true,
            data: {
                agentSS: this.lgvUserSvc.AgentSS$.value,
                pricingScenario: scenario,
                opportunity: o,
                loanApplication: loanApps.find(
                    la => la?.id == scenario?.loanApplicationId
                )
            }
        });

        dialogRef
            .afterClosed()
            .pipe(first())
            .subscribe(x => {
                this.lgvClientSvc.OpportunitiesState$.next('Ready');
                this.quoteLocked.emit(true);
            });
    }

    contactAgent(o: LgvModel.Opportunity) {
        const product = o?.opportunityInfo;
        let message = ``;

        const dialogRef = this.matDialog.open(ContactAgentDialogComponent, {
            width: '500px',
            data: {
                opportunity: o,
                message: message
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.lgvClientSvc
                    .PostPageClick('Rate Quote Email Sent')
                    .pipe(first())
                    .subscribe();
                this.notificationSvc.error('Rate Quote Email Sent');
            }
        });
    }
}
